import React, { useState } from 'react'
import { formatCurrencyFromCents } from '../../utils/api'
import { toPence } from '../pricing/repriceMath'
import { CARRIERS, formatNumber } from './carriers.js'

const HEADING_STYLE = { fontSize: 12, fontWeight: 600, color: 'var(--color-text-muted)' }
const CARD_STYLE = {
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius)',
  padding: 'var(--space-3)',
  marginBottom: 'var(--space-2)',
}
const CARRIER_CHOICES = Object.values(CARRIERS).filter(c => c.id !== 'warehouse_dropoff')
const GRAMS_PER_KG = 1000
const MM_PER_CM = 10

/**
 * The boxes of a collection, one card each (Trac #1077 part 1, #1337).
 * `state` is what `useCollectionParcels` returns. Renders nothing for a
 * collection with no booking yet.
 */
export default function CollectionParcels({ state }) {
  const { parcels, missing, error, busy, writeError, addBox, setBoxItems, setBoxLabel } = state
  const [labelFormBox, setLabelFormBox] = useState(null)

  if (missing) return null
  if (error) return <p role="alert" style={{ fontSize: 12, color: 'var(--color-error)' }}>Could not load the boxes: {error}</p>
  if (!parcels) return null

  const boxes = parcels.parcels
  const allItems = shipmentItems(parcels)
  const boxesWithoutLabel = boxes.filter(box => !hasLabel(box))

  async function saveLabel(boxNumber, label) {
    if (await setBoxLabel(boxNumber, label)) setLabelFormBox(null)
  }

  return (
    <div style={{ marginBottom: 'var(--space-4)' }} data-testid="collection-parcels">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <div style={HEADING_STYLE}>{sectionHeading(boxes)}</div>
        {parcels.contents_needed && <span className="badge badge-warning">Over £500: contents needed</span>}
      </div>

      {boxes.map(box => (
        <BoxCard
          key={box.box_number}
          box={box}
          allItems={allItems}
          contentsNeeded={parcels.contents_needed}
          busy={busy}
          editingLabel={labelFormBox === box.box_number}
          onToggleItem={itemId => setBoxItems(box.box_number, toggledItemIds(box, itemId))}
          onEditLabel={() => setLabelFormBox(box.box_number)}
          onCancelLabel={() => setLabelFormBox(null)}
          onSaveLabel={label => saveLabel(box.box_number, label)}
        />
      ))}

      {parcels.unplaced_items.length > 0 && <UnplacedItems items={parcels.unplaced_items} />}

      {writeError && <p role="alert" style={{ fontSize: 12, color: 'var(--color-error)', margin: '0 0 8px' }}>{writeError}</p>}

      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        {boxesWithoutLabel.map(box => (
          <button key={box.box_number} type="button" className="btn btn-secondary btn-xs" style={{ padding: '6px 10px' }}
            disabled={busy} onClick={() => setLabelFormBox(box.box_number)}>
            + Add label to Box {box.box_number}
          </button>
        ))}
        <button type="button" className="btn btn-secondary btn-xs" style={{ padding: '6px 10px' }} disabled={busy} onClick={addBox}>
          + Add box
        </button>
        <span style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--color-text-muted)' }}>
          Shipping total {formatCurrencyFromCents(parcels.shipping_total_cents)}
        </span>
      </div>
    </div>
  )
}

function BoxCard({ box, allItems, contentsNeeded, busy, editingLabel, onToggleItem, onEditLabel, onCancelLabel, onSaveLabel }) {
  const labelled = hasLabel(box)
  return (
    <div style={CARD_STYLE} data-testid={`box-${box.box_number}`}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
        <strong style={{ fontSize: 14 }}>{boxTitle(box)}</strong>
        <span className={`badge ${labelled ? 'badge-success' : 'badge-neutral'}`}>
          {labelled ? 'Label ✓' : 'Needs own label'}
        </span>
      </div>

      {contentsNeeded
        ? <ContentsChecklist box={box} allItems={allItems} busy={busy} onToggleItem={onToggleItem} />
        : <ContentsText items={box.items} />}

      {labelled && <LabelDetails box={box} onEdit={onEditLabel} />}
      {editingLabel && <LabelForm box={box} busy={busy} onSave={onSaveLabel} onCancel={onCancelLabel} />}
    </div>
  )
}

function ContentsText({ items }) {
  if (items.length === 0) return null
  return (
    <div style={{ fontSize: 13, marginTop: 6 }}>
      Contains: {items.map(item => itemText(item)).join(', ')}
    </div>
  )
}

function ContentsChecklist({ box, allItems, busy, onToggleItem }) {
  const inBox = new Set(box.items.map(item => item.quote_item_id))
  return (
    <fieldset style={{ border: 'none', margin: '6px 0 0', padding: 0 }}>
      <legend style={{ fontSize: 13, padding: 0 }}>Contains:</legend>
      {allItems.map(item => (
        <label key={item.quote_item_id} style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13, padding: '2px 0' }}>
          <input
            type="checkbox"
            checked={inBox.has(item.quote_item_id)}
            disabled={busy}
            onChange={() => onToggleItem(item.quote_item_id)}
          />
          {itemText(item)}
        </label>
      ))}
    </fieldset>
  )
}

function LabelDetails({ box, onEdit }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 6, fontSize: 13 }}>
      {box.tracking_number && <span style={{ fontFamily: 'monospace' }}>{box.tracking_number}</span>}
      {box.label_price_cents != null && <span>{formatCurrencyFromCents(box.label_price_cents)}</span>}
      <button type="button" className="btn btn-xs" style={{ marginLeft: 'auto' }} onClick={onEdit}>
        Edit label
      </button>
    </div>
  )
}

function UnplacedItems({ items }) {
  return (
    <div role="status" className="badge-warning" style={{ ...CARD_STYLE, fontSize: 13 }}>
      <strong>Not in any box yet:</strong> {items.map(item => itemText(item)).join(', ')}
    </div>
  )
}

function LabelForm({ box, busy, onSave, onCancel }) {
  const [carrier, setCarrier] = useState(matchingCarrierName(box.carrier))
  const [trackingNumber, setTrackingNumber] = useState(box.tracking_number ?? '')
  const [price, setPrice] = useState(box.label_price_cents == null ? '' : (box.label_price_cents / 100).toFixed(2))
  const [formError, setFormError] = useState(null)
  const idPrefix = `box-${box.box_number}`

  function submit(event) {
    event.preventDefault()
    const labelPriceCents = toPence(price)
    if (price.trim() !== '' && (labelPriceCents == null || labelPriceCents < 0)) {
      setFormError('The label price must be an amount in pounds')
      return
    }
    setFormError(null)
    onSave(labelPayload({ carrier, trackingNumber, labelPriceCents }))
  }

  return (
    <form noValidate onSubmit={submit} style={{ marginTop: 8, display: 'grid', gap: 6 }} aria-label={`Label for box ${box.box_number}`}>
      <div className="form-group" style={{ margin: 0 }}>
        <label htmlFor={`${idPrefix}-carrier`} style={{ fontSize: 12 }}>Carrier</label>
        <select id={`${idPrefix}-carrier`} className="form-input" value={carrier} onChange={e => setCarrier(e.target.value)}>
          <option value="">Choose a carrier</option>
          {CARRIER_CHOICES.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
        </select>
      </div>
      <div className="form-group" style={{ margin: 0 }}>
        <label htmlFor={`${idPrefix}-tracking`} style={{ fontSize: 12 }}>Tracking number</label>
        <input id={`${idPrefix}-tracking`} type="text" className="form-input" value={trackingNumber}
          onChange={e => setTrackingNumber(e.target.value)} />
      </div>
      <div className="form-group" style={{ margin: 0 }}>
        <label htmlFor={`${idPrefix}-price`} style={{ fontSize: 12 }}>Label price (£)</label>
        <input id={`${idPrefix}-price`} type="number" step="0.01" min="0" className="form-input" value={price}
          onChange={e => setPrice(e.target.value)} placeholder="0.00" />
      </div>
      {formError && <p role="alert" style={{ fontSize: 12, color: 'var(--color-error)', margin: 0 }}>{formError}</p>}
      <div style={{ display: 'flex', gap: 8 }}>
        <button type="submit" className="btn btn-primary btn-xs" style={{ padding: '6px 10px' }} disabled={busy}>Save label</button>
        <button type="button" className="btn btn-secondary btn-xs" style={{ padding: '6px 10px' }} onClick={onCancel}>Cancel</button>
      </div>
    </form>
  )
}

// ---- Pure helpers ----

function hasLabel(box) {
  return !!(box.tracking_number || box.label_link)
}

function sectionHeading(boxes) {
  const grams = boxes.reduce((sum, box) => sum + (box.weight_grams ?? 0), 0)
  const noun = boxes.length === 1 ? 'BOX' : 'BOXES'
  return `PARCELS · ${boxes.length} ${noun} · ${formatNumber(grams / GRAMS_PER_KG)} KG`
}

function boxTitle(box) {
  const parts = [`Box ${box.box_number}`]
  if (box.weight_grams != null) parts.push(`${formatNumber(box.weight_grams / GRAMS_PER_KG)} kg`)
  const size = [box.length_mm, box.width_mm, box.height_mm]
  if (size.every(side => side != null)) parts.push(size.map(side => formatNumber(side / MM_PER_CM)).join('×'))
  return parts.join(' · ')
}

function itemText(item) {
  const price = item.buy_price_cents == null ? '' : ` (${formatCurrencyFromCents(item.buy_price_cents)})`
  return `${item.name}${price}`
}

// Every item of the booking: the ones in a box, then the ones in none.
function shipmentItems(parcels) {
  const seen = new Set()
  return [...parcels.parcels.flatMap(box => box.items), ...parcels.unplaced_items].filter(item => {
    if (seen.has(item.quote_item_id)) return false
    seen.add(item.quote_item_id)
    return true
  })
}

// The box's whole new contents. Prism moves an item that is in another box.
function toggledItemIds(box, itemId) {
  const ids = box.items.map(item => item.quote_item_id)
  return ids.includes(itemId) ? ids.filter(id => id !== itemId) : [...ids, itemId]
}

function matchingCarrierName(carrier) {
  const match = CARRIER_CHOICES.find(c => c.name.toLowerCase() === String(carrier ?? '').toLowerCase())
  return match ? match.name : ''
}

// Only what the form holds: Prism changes just the fields it is sent.
function labelPayload({ carrier, trackingNumber, labelPriceCents }) {
  const payload = {}
  if (carrier) payload.carrier = carrier
  if (trackingNumber.trim()) payload.tracking_number = trackingNumber.trim()
  if (labelPriceCents != null) payload.label_price_cents = labelPriceCents
  return payload
}
